import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import {
  MdAdd,
  MdEdit,
  MdPeople,
  MdDelete,
  MdClose,
  MdCheck,
  MdUpload,
  MdDownload,
  MdCheckCircle,
  MdPersonAdd,
  MdSync,
  MdRefresh
} from 'react-icons/md';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import FileUploadField from '../../components/FileUploadField';
import notify from '../../utils/IANotification';
import {
  fetch_ministry_servers,
  update_ministry_server,
  delete_ministry_server,
  create_ministry_server,
  upload_ministries_csv
} from '../../api/ministryAPI';

export default function MinistryPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  const [ministryData, setMinistryData] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedMinistry, setSelectedMinistry] = useState(null)
  const [serversList, setServersList] = useState([])
  const [loadingServers, setLoadingServers] = useState(false)
  const [editingServer, setEditingServer] = useState(null)
  const [newServer, setNewServer] = useState({
    personnel_name: '',
    role: '',
    start_date: '2023-01-01',
    end_date: '',
    is_current: '1'
  })

  // CSV Import State
  const [showCsvModal, setShowCsvModal] = useState(false)
  const [csvText, setCsvText] = useState('')
  const [csvUploading, setCsvUploading] = useState(false)
  const [csvResult, setCsvResult] = useState(null)

  useEffect(() => {
    fetchMinistry()
  }, [])

  const fetchMinistry = async () => {
    setLoading(true)
    let endpoint = config.endpoint + "/ministry/"
    let response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    })
    let data = await response.json()
    if (data.status === 200) {
      setMinistryData(data.data || [])
    }
    setLoading(false)
  }

  const downloadSampleCsv = () => {
    const sampleRows = [
      "Name,Tagline,Type,Description,Link,Parent Ministry",
      "Youth Ministry,Discipling the next generation for Christ,equip,\"Nurturing youths aged 13 to 25 through cell groups, mentoring, and youth worship services.\",https://bmc.org.sg/youth,",
      "Worship & Creative Arts,Leading in spirit and truth,connect,\"Leading the congregation in worship through music, AV, production, and creative arts.\",https://bmc.org.sg/worship,",
      "Outreach & Missions,Serving our community and reaching the nations,release,\"Impacting the local Bedok community and supporting regional mission partnerships.\",https://bmc.org.sg/missions,",
      "Children's Ministry,Nurturing little ones in God's love,equip,\"Sunday school and faith foundations for toddlers through primary schoolers.\",https://bmc.org.sg/children,",
      "Witness & Evangelism,Sharing the Gospel with boldness and love,release,\"Training worshippers in personal evangelism and community outreach.\",,Outreach & Missions"
    ]
    const content = sampleRows.join("\n")
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'sample_ministries.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFileSelected = (files) => {
    if (files && files.length > 0) {
      const file = files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        setCsvText(e.target.result || '')
      }
      reader.readAsText(file)
    }
  }

  const handleCsvUpload = async () => {
    if (!csvText.trim()) {
      notify({ type: "error", message: "Please select or paste CSV content first" })
      return
    }

    setCsvUploading(true)
    setCsvResult(null)

    const res = await upload_ministries_csv(csvText)
    setCsvUploading(false)

    if (res?.status === 200) {
      setCsvResult({ success: true, message: res.message || "Ministries imported successfully!" })
      notify({ type: "success", message: res.message || "Ministries imported successfully!" })
      fetchMinistry()
      setTimeout(() => {
        setShowCsvModal(false)
        setCsvText('')
        setCsvResult(null)
      }, 1600)
    } else {
      setCsvResult({ success: false, message: res?.message || "Failed to import ministries CSV" })
      notify({ type: "error", message: res?.message || "Failed to import ministries CSV" })
    }
  }

  const loadMinistryServers = async (ministry_id) => {
    setLoadingServers(true)
    let res = await fetch_ministry_servers(ministry_id)
    if (res?.data && Array.isArray(res.data)) {
      setServersList(res.data)
    }
    setLoadingServers(false)
  }

  const handleOpenServersModal = (ministry) => {
    setSelectedMinistry(ministry)
    loadMinistryServers(ministry.id)
  }

  const handleSaveEditServer = async () => {
    if (!editingServer) return
    let res = await update_ministry_server(editingServer.id, {
      role: editingServer.role,
      personnel_name: editingServer.personnel_name,
      start_date: editingServer.start_date,
      end_date: editingServer.is_current === "1" ? null : editingServer.end_date,
      is_current: editingServer.is_current
    })
    if (res?.status === 200) {
      setEditingServer(null)
      loadMinistryServers(selectedMinistry.id)
    }
  }

  const handleDeleteServer = async (id) => {
    let res = await delete_ministry_server(id)
    if (res?.status === 200) {
      loadMinistryServers(selectedMinistry.id)
    }
  }

  const handleCreateServer = async () => {
    if (!newServer.role.trim()) return
    let res = await create_ministry_server({
      ministry_id: selectedMinistry.id,
      personnel_name: newServer.personnel_name || "Declared User",
      role: newServer.role,
      start_date: newServer.start_date,
      end_date: newServer.is_current === "1" ? null : newServer.end_date,
      is_current: newServer.is_current
    })
    if (res?.status === 200 || res?.status === 201) {
      setNewServer({ personnel_name: '', role: '', start_date: '2023-01-01', end_date: '', is_current: '1' })
      loadMinistryServers(selectedMinistry.id)
    }
  }

  const table_columns = [
    {
      header: "Name",
      accessor: "name",
      modifier: (val) => <span className="font-semibold text-gray-900">{val}</span>
    },
    {
      header: "Tagline",
      accessor: "tagline"
    },
    {
      header: "Type",
      accessor: "type",
      modifier: (val) => {
        const typeColors = {
          connect: 'bg-blue-100 text-blue-800',
          equip: 'bg-purple-100 text-purple-800',
          release: 'bg-emerald-100 text-emerald-800',
        }
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
            typeColors[val?.toLowerCase()] || 'bg-gray-100 text-gray-800'
          }`}>
            {val || 'General'}
          </span>
        )
      }
    },
    {
      header: "Parent Ministry",
      accessor: "parent_ministry_id",
      data: (row) => {
        if (!row?.parent_ministry_id) return <span className="text-gray-400 text-xs italic">None</span>
        const parent = ministryData.find(m => String(m.id) === String(row?.parent_ministry_id))
        return parent ? <span className="text-xs font-medium text-gray-700">{parent.name}</span> : "None"
      }
    },
  ]

  return (
    <AdminLayout
      navSelected={"Ministry"}
      toolbar={
        <>
          <h1 className='text-lg font-semibold text-gray-900'>Ministries</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              icon={MdUpload}
              onClick={() => {
                setCsvText('')
                setCsvResult(null)
                setShowCsvModal(true)
              }}
            >
              Import CSV
            </Button>
            <Button
              variant="primary"
              icon={MdAdd}
              onClick={() => {
                navigate("/admin/ministry/details?ministry_id=new")
              }}
            >
              New Ministry
            </Button>
          </div>
        </>
      }
    >
      {loading ? (
        <div className="p-8 text-center text-gray-500 font-medium">Loading Ministries...</div>
      ) : (
        <div className="w-100">
          <DataTable
            columns={table_columns}
            data={ministryData}
            enableActions
            actions={(row) => {
              return (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={MdPeople}
                    onClick={() => handleOpenServersModal(row)}
                  >
                    Servers & Records
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={MdEdit}
                    onClick={() => navigate(`/admin/ministry/details?ministry_id=${row.id}`)}
                  >
                    Edit
                  </Button>
                </div>
              )
            }}
          />
        </div>
      )}

      {/* CSV Import Modal (Same design as Missions and Staff) */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative border border-gray-200 space-y-4 max-h-[92vh] overflow-y-auto">
            <button
              onClick={() => setShowCsvModal(false)}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <MdClose size={22} />
            </button>

            <div className="flex items-center justify-between pr-8">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Import Ministries via CSV</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Upload a CSV file or paste content below. Expected headers: <code className="bg-gray-100 px-1 rounded text-gray-800 font-mono">Name, Tagline, Type, Description, Link, Parent Ministry</code>
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                icon={MdDownload}
                onClick={downloadSampleCsv}
              >
                Sample CSV
              </Button>
            </div>

            {/* Informational Upsert Notice */}
            <div className="p-3 bg-blue-50/70 rounded-xl border border-blue-100 text-xs text-blue-800 flex items-start gap-2">
              <span className="font-semibold shrink-0">Automatic Upsert:</span>
              <span>The system will automatically update existing ministries (matched by Name) or add them as new ministries if they do not exist.</span>
            </div>

            {/* File Upload Component */}
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
              <FileUploadField
                item_index="ministries_csv"
                max_file_size={5}
                max_files={1}
                text="Select or Drag Ministries CSV File"
                subtext="Supported format: .csv"
                onChange={(files) => handleFileSelected(files)}
              />
            </div>

            {/* Text Preview / Edit Area */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                CSV Content Preview / Manual Edit:
              </label>
              <textarea
                rows={6}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder={`Name,Tagline,Type,Description,Link,Parent Ministry\nYouth Ministry,Discipling youths,equip,"Youth fellowship and worship...",https://bmc.org.sg/youth,\nWorship Ministry,Leading in worship,connect,"Choir, AV and worship band...",,`}
                className="w-full border border-gray-200 rounded-xl p-3 text-xs font-mono bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {csvResult && (
              <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                csvResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}>
                <MdCheckCircle size={18} />
                {csvResult.message}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" icon={MdClose} onClick={() => setShowCsvModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                icon={MdUpload}
                onClick={handleCsvUpload}
                disabled={csvUploading || !csvText.trim()}
              >
                {csvUploading ? 'Processing...' : 'Process & Import Ministries'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ministry Servers & Declared History Modal */}
      {selectedMinistry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 shadow-2xl relative border border-gray-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedMinistry.name}</h3>
                <p className="text-xs text-gray-500">Declared Ministry Servers &amp; Active History</p>
              </div>
              <button
                onClick={() => setSelectedMinistry(null)}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Add New Server Record Form */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Add / Declare Ministry Server</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Personnel Name / User"
                    value={newServer.personnel_name}
                    onChange={(e) => setNewServer({ ...newServer, personnel_name: e.target.value })}
                    className="p-2 border rounded-lg text-xs"
                  />
                  <input
                    type="text"
                    placeholder="Role (e.g. Leader, Usher)"
                    value={newServer.role}
                    onChange={(e) => setNewServer({ ...newServer, role: e.target.value })}
                    className="p-2 border rounded-lg text-xs"
                  />
                  <input
                    type="text"
                    placeholder="Start Date (YYYY-MM-DD)"
                    value={newServer.start_date}
                    onChange={(e) => setNewServer({ ...newServer, start_date: e.target.value })}
                    className="p-2 border rounded-lg text-xs"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-xs font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={newServer.is_current === '1'}
                      onChange={(e) => setNewServer({ ...newServer, is_current: e.target.checked ? '1' : '0' })}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span>Currently Active Serving</span>
                  </label>
                  <Button variant="primary" size="sm" onClick={handleCreateServer}>
                    Save Server
                  </Button>
                </div>
              </div>

              {/* Existing Server Records List */}
              {loadingServers ? (
                <div className="p-8 text-center text-xs text-gray-400">Loading servers...</div>
              ) : serversList.length > 0 ? (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">Personnel / Member</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">Role</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">Duration</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-600">Status</th>
                        <th className="px-4 py-2 text-right font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {serversList.map((server) => {
                        const isEditing = editingServer?.id === server.id
                        return (
                          <tr key={server.id}>
                            <td className="px-4 py-2 font-medium text-gray-900">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingServer.personnel_name}
                                  onChange={(e) => setEditingServer({ ...editingServer, personnel_name: e.target.value })}
                                  className="p-1 border rounded text-xs w-full"
                                />
                              ) : (
                                server.user ? `${server.user.first_name || ''} ${server.user.last_name || ''}`.trim() || server.personnel_name : server.personnel_name || "Declared User"
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editingServer.role}
                                  onChange={(e) => setEditingServer({ ...editingServer, role: e.target.value })}
                                  className="p-1 border rounded text-xs w-full"
                                />
                              ) : (
                                server.role
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-500">
                              {server.start_date || 'N/A'} - {server.is_current === '1' ? 'Present' : (server.end_date || 'N/A')}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                                server.is_current === '1' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {server.is_current === '1' ? 'Active' : 'Past'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={handleSaveEditServer}
                                      className="p-1 text-emerald-600 hover:text-emerald-800"
                                      title="Save Edit"
                                    >
                                      <MdCheck size={16} />
                                    </button>
                                    <button
                                      onClick={() => setEditingServer(null)}
                                      className="p-1 text-gray-400 hover:text-gray-600"
                                      title="Cancel"
                                    >
                                      <MdClose size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => setEditingServer({ ...server })}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Edit Record"
                                  >
                                    <MdEdit size={16} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteServer(server.id)}
                                  className="p-1 text-red-400 hover:text-red-600"
                                  title="Delete Record"
                                >
                                  <MdDelete size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-xs text-gray-400">No server records declared for this ministry</div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t text-right">
              <Button variant="outline" size="sm" onClick={() => setSelectedMinistry(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
